import { c as _c } from "react/compiler-runtime";
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { type RefObject, useEffect, useRef, useState } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useMobileViewport from '@/hooks/use-mobile-viewport';
import { activeBrowserAsk, liveBrowserSession } from '../utils/chat-segments';
import useChatWidgetLayout from './use-chat-widget-layout';
import useChatWidgetLayoutSwitch from './use-chat-widget-layout-switch';
import { suppressNextChatWidgetMorph } from './use-chat-widget-morphing';
import type { ChatMessage } from './use-chat-ws';
import useInvestigationDock from './use-investigation-dock';
import useSlotRect from './use-slot-rect';
const SPLIT_MIN_WIDTH = 840;
const VIEWPORT_SYNC_MS = 300;
const MIN_FRAME_SIZE = 200;
// The live view reloads for every size the remote takes, so the pane's small size changes are
// absorbed by scaling the frame instead; only a change this large reshapes the page.
const VIEWPORT_TOLERANCE = 0.15;
// What the remote page starts at, so a site lays out for a desktop before the pane is opened.
const DEFAULT_VIEWPORT = {
  width: 1280,
  height: 800
};
type BrowserViewportSize = {
  width: number;
  height: number;
};
type BrowserPaneSlots = {
  rootRef: RefObject<HTMLDivElement | null>;
  /** The pane's frame area; its size is what the remote page is asked to take. */
  paneSlotRef: RefObject<HTMLDivElement | null>;
  cardSlotRef: RefObject<HTMLDivElement | null>;
};
const useBrowserPane = (slots, messages, chatSession) => {
  const $ = _c(75);
  const [split, setSplit] = useState(false);
  const [wide, setWide] = useState(false);
  const isMobile = useMobileLayout();
  const keyboardOpen = useMobileViewport()?.keyboardOpen ?? false;
  const [remoteViewport, setRemoteViewport] = useState(DEFAULT_VIEWPORT);
  const [resizing, setResizing] = useState(false);
  const [openedFor, setOpenedFor] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(null);
  const {
    mode,
    setMode,
    restoreMode,
    setRestoreMode
  } = useChatWidgetLayout();
  const switchLayout = useChatWidgetLayoutSwitch();
  const lockedSidebar = useInvestigationDock();
  let t0;
  if ($[0] !== chatSession.modalBaseUrl || $[1] !== messages) {
    t0 = chatSession.modalBaseUrl === "" ? null : liveBrowserSession(messages);
    $[0] = chatSession.modalBaseUrl;
    $[1] = messages;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const latest = t0;
  const sessionId = latest?.id ?? null;
  const opened = sessionId !== null && openedFor === sessionId;
  const dismissed = latest !== null && dismissedAt?.id === latest.id && latest.step <= dismissedAt.step;
  let t1;
  if ($[3] !== chatSession.sessionId || $[4] !== sessionId) {
    t1 = ["percy-browser-live-view", chatSession.sessionId, sessionId];
    $[3] = chatSession.sessionId;
    $[4] = sessionId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== chatSession || $[7] !== sessionId) {
    t2 = () => fetchLiveViewUrl(chatSession, sessionId ?? "");
    $[6] = chatSession;
    $[7] = sessionId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = sessionId !== null;
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: Number.POSITIVE_INFINITY,
      retry: 2
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const liveView = useQuery(t4);
  const session = liveView.data === null ? null : latest;
  let t5;
  if ($[13] !== messages || $[14] !== session) {
    t5 = session === null ? null : activeBrowserAsk(messages);
    $[13] = messages;
    $[14] = session;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const ask = t5;
  const pinnedFor = useRef(null);
  const requestedFor = useRef(null);
  let t6;
  if ($[16] !== chatSession.sessionId || $[17] !== slots.rootRef.current) {
    t6 = () => {
      const el = slots.rootRef.current;
      if (!el || chatSession.sessionId === "") {
        return;
      }
      const observer = new ResizeObserver(entries => setSplit(entries[0].contentRect.width >= SPLIT_MIN_WIDTH));
      observer.observe(el);
      return () => observer.disconnect();
    };
    $[16] = chatSession.sessionId;
    $[17] = slots.rootRef.current;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== chatSession.sessionId || $[20] !== slots.rootRef) {
    t7 = [slots.rootRef, chatSession.sessionId];
    $[19] = chatSession.sessionId;
    $[20] = slots.rootRef;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  useEffect(t6, t7);
  const paneOpen = session !== null && opened && (split || mode === "fullscreen");
  const coversChat = isMobile && paneOpen;
  const previewOpen = session !== null && !paneOpen && !dismissed;
  const frameSlot = paneOpen ? "pane" : previewOpen ? "card" : null;
  const frame = useSlotRect(paneOpen ? slots.paneSlotRef : slots.cardSlotRef, slots.rootRef, frameSlot);
  const paneRect = frame.slotKey === "pane" ? frame.rect : null;
  let t8;
  let t9;
  if ($[22] !== mode || $[23] !== opened || $[24] !== restoreMode || $[25] !== setMode || $[26] !== setRestoreMode) {
    t8 = () => {
      if (restoreMode === null) {
        return;
      }
      if (mode !== "fullscreen") {
        setRestoreMode(null);
        return;
      }
      if (opened) {
        return;
      }
      suppressNextChatWidgetMorph();
      setMode(restoreMode);
      setRestoreMode(null);
    };
    t9 = [restoreMode, mode, opened, setMode, setRestoreMode];
    $[22] = mode;
    $[23] = opened;
    $[24] = restoreMode;
    $[25] = setMode;
    $[26] = setRestoreMode;
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[29] !== chatSession || $[30] !== sessionId) {
    t10 = () => {
      if (sessionId === null) {
        pinnedFor.current = null;
        requestedFor.current = null;
        return;
      }
      if (pinnedFor.current === sessionId) {
        return;
      }
      pinnedFor.current = sessionId;
      postViewport(chatSession, sessionId, DEFAULT_VIEWPORT).then(taken => {
        if (requestedFor.current === null) {
          setRemoteViewport(taken);
        }
      }).catch(_temp);
    };
    t11 = [sessionId, chatSession];
    $[29] = chatSession;
    $[30] = sessionId;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  if ($[33] !== paneRect) {
    t12 = paneRect === null ? 0 : Math.round(paneRect.width);
    $[33] = paneRect;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const paneWidth = t12;
  let t13;
  if ($[35] !== paneRect) {
    t13 = paneRect === null ? 0 : Math.round(paneRect.height);
    $[35] = paneRect;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const paneHeight = t13;
  let t14;
  let t15;
  if ($[37] !== chatSession || $[38] !== keyboardOpen || $[39] !== paneHeight || $[40] !== paneOpen || $[41] !== paneWidth || $[42] !== remoteViewport || $[43] !== sessionId) {
    t14 = () => {
      if (!paneOpen || keyboardOpen || sessionId === null || paneWidth < MIN_FRAME_SIZE || paneHeight < MIN_FRAME_SIZE) {
        return;
      }
      const size = {
        width: paneWidth,
        height: paneHeight
      };
      const sizeKey = `${sessionId}:${paneWidth}x${paneHeight}`;
      if (requestedFor.current === sizeKey || !offBy(remoteViewport, size, VIEWPORT_TOLERANCE)) {
        return;
      }
      const post = () => {
        requestedFor.current = sizeKey;
        setResizing(true);
        postViewport(chatSession, sessionId, size).then(setRemoteViewport).catch(_temp2).finally(() => setResizing(false));
      };
      if (requestedFor.current === null) {
        post();
        return;
      }
      const timer = setTimeout(post, VIEWPORT_SYNC_MS);
      return () => clearTimeout(timer);
    };
    t15 = [paneOpen, keyboardOpen, paneWidth, paneHeight, sessionId, remoteViewport, chatSession];
    $[37] = chatSession;
    $[38] = keyboardOpen;
    $[39] = paneHeight;
    $[40] = paneOpen;
    $[41] = paneWidth;
    $[42] = remoteViewport;
    $[43] = sessionId;
    $[44] = t14;
    $[45] = t15;
  } else {
    t14 = $[44];
    t15 = $[45];
  }
  useEffect(t14, t15);
  let t16;
  if ($[46] !== lockedSidebar || $[47] !== mode || $[48] !== sessionId || $[49] !== setRestoreMode || $[50] !== split || $[51] !== switchLayout) {
    t16 = () => {
      setDismissedAt(null);
      setOpenedFor(sessionId);
      if (split || mode === "fullscreen") {
        return;
      }
      suppressNextChatWidgetMorph();
      setRestoreMode(lockedSidebar ? "sidebar" : mode);
      switchLayout("fullscreen");
    };
    $[46] = lockedSidebar;
    $[47] = mode;
    $[48] = sessionId;
    $[49] = setRestoreMode;
    $[50] = split;
    $[51] = switchLayout;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  const expand = t16;
  const t17 = liveView.data ?? null;
  const t18 = wide || coversChat;
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setWide(_temp3);
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== chatSession || $[55] !== sessionId) {
    t20 = code => postHandoffCode(chatSession, sessionId ?? "", code);
    $[54] = chatSession;
    $[55] = sessionId;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setOpenedFor(null);
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== latest) {
    t22 = () => {
      if (latest !== null) {
        setDismissedAt({
          id: latest.id,
          step: latest.step
        });
      }
      setOpenedFor(null);
    };
    $[58] = latest;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== ask || $[61] !== coversChat || $[62] !== expand || $[63] !== frame.rect || $[64] !== liveView.isError || $[65] !== paneOpen || $[66] !== previewOpen || $[67] !== remoteViewport || $[68] !== resizing || $[69] !== session || $[70] !== t17 || $[71] !== t18 || $[72] !== t20 || $[73] !== t22) {
    t23 = {
      session,
      ask,
      liveViewUrl: t17,
      liveViewFailed: liveView.isError,
      paneOpen,
      previewOpen,
      frameRect: frame.rect,
      remoteViewport,
      resizing,
      wide: t18,
      coversChat,
      toggleWide: t19,
      submitCode: t20,
      expand,
      minimize: t21,
      dismiss: t22
    };
    $[60] = ask;
    $[61] = coversChat;
    $[62] = expand;
    $[63] = frame.rect;
    $[64] = liveView.isError;
    $[65] = paneOpen;
    $[66] = previewOpen;
    $[67] = remoteViewport;
    $[68] = resizing;
    $[69] = session;
    $[70] = t17;
    $[71] = t18;
    $[72] = t20;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  return t23;
};
const offBy = (remote: BrowserViewportSize, target: BrowserViewportSize, tolerance: number): boolean => Math.abs(remote.width - target.width) / target.width > tolerance || Math.abs(remote.height - target.height) / target.height > tolerance;
const browserUrl = (chatSession: ChatSessionResponse, browserSessionId: string, path: string): string => `${chatSession.modalBaseUrl}/sessions/${chatSession.sessionId}/browser/${encodeURIComponent(browserSessionId)}/${path}`;
const fetchLiveViewUrl = async (chatSession: ChatSessionResponse, browserSessionId: string): Promise<string | null> => {
  const response = await fetch(browserUrl(chatSession, browserSessionId, 'live-view'), {
    headers: {
      Authorization: `Bearer ${chatSession.sessionToken}`
    }
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to load the live view (${response.status})`);
  const body = (await response.json()) as {
    live_view_url: string;
  };
  return body.live_view_url;
};

/** Asks the remote page to take `size` and resolves with the size it actually took. */
const postViewport = async (chatSession: ChatSessionResponse, browserSessionId: string, size: BrowserViewportSize): Promise<BrowserViewportSize> => {
  const response = await fetch(browserUrl(chatSession, browserSessionId, 'viewport'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${chatSession.sessionToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(size)
  });
  if (!response.ok) throw new Error(`Failed to resize the browser (${response.status})`);
  const {
    width,
    height
  } = (await response.json()) as BrowserViewportSize;
  return {
    width,
    height
  };
};
const postHandoffCode = async (chatSession: ChatSessionResponse, browserSessionId: string, code: string): Promise<void> => {
  const response = await fetch(browserUrl(chatSession, browserSessionId, 'code'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${chatSession.sessionToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      code
    })
  });
  if (!response.ok) throw new Error(`Failed to enter the code (${response.status})`);
};
export type { BrowserViewportSize };
export default useBrowserPane;
function _temp() {}
function _temp2() {}
function _temp3(current) {
  return !current;
}